
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Service Area', path: '/#service-area' },
    { name: 'Reviews', path: '/#reviews' },
    { name: 'Gallery', path: '/#gallery' },
    { name: 'Contact', path: '/#contact' },
  ];

  const handleNavClick = (path: string) => {
    setIsMobileMenuOpen(false);
    if (path.includes('#')) {
      const [route, hash] = path.split('#');
      if (location.pathname !== route && route) {
        window.REACT_APP_NAVIGATE(route);
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <>
      <motion.nav
        initial={{ backgroundColor: 'rgba(0, 0, 0, 0)' }}
        animate={{
          backgroundColor: isScrolled ? 'rgba(255, 255, 255, 1)' : 'rgba(0, 0, 0, 0)',
          boxShadow: isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 0 0 rgba(0, 0, 0, 0)',
        }}
        transition={{ duration: 0.3 }}
        className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-16 py-4"
      >
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/c96bfb198278da73880d302267f14c20.png"
              alt="A and B Concrete LLC"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center gap-10 absolute left-1/2 transform -translate-x-1/2">
            {navLinks.map((link) => (
              link.path.includes('#') ? (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    isScrolled ? 'text-gray-800 hover:text-brand-green' : 'text-white hover:text-brand-green'
                  }`}
                >
                  {link.name}
                </button>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${
                    isScrolled ? 'text-gray-800 hover:text-brand-green' : 'text-white hover:text-brand-green'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* Call Button */}
          <div className="hidden lg:block">
            <a
              href="tel:5743776776"
              className="bg-brand-green text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-brand-green/90 transition-all whitespace-nowrap cursor-pointer"
            >
              (574) 377-6776
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-800' : 'text-white'}`}></i>
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-white z-50 lg:hidden"
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <img
                  src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/c96bfb198278da73880d302267f14c20.png"
                  alt="A and B Concrete LLC"
                  className="h-12 w-auto"
                />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-10 h-10 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-gray-800"></i>
                </button>
              </div>
              <div className="flex flex-col gap-2 p-6">
                {navLinks.map((link) => (
                  link.path.includes('#') ? (
                    <button
                      key={link.name}
                      onClick={() => handleNavClick(link.path)}
                      className="text-left text-lg font-medium text-gray-800 hover:text-brand-green py-3 cursor-pointer"
                    >
                      {link.name}
                    </button>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-lg font-medium text-gray-800 hover:text-brand-green py-3"
                    >
                      {link.name}
                    </Link>
                  )
                ))}
                <a
                  href="tel:5743776776"
                  className="bg-brand-green text-white px-6 py-4 rounded-full text-center font-semibold mt-4 cursor-pointer"
                >
                  (574) 377-6776
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
